import * as ajaxEndpoints from "../../api/ajax-endpoints";
import {
  postDataWithDotNet,
  getDataWithDotNet,
} from "../../newApis/dotNetApiMethods";
import { errorHandler } from "../../helpers/errorHandler";
import { appInsights } from "../../config/appInsights";
import {
  updateFormData,
  calculateInterest,
  setCurrentStep,
  setAvailableWallets,
} from "./investmentForm";
import { message } from "antd";
import { isDynamicDeposit } from "../../helpers/depositType";
import { IAdminLogInvestmentRequest } from "../../interfaces/funderInvestment";

/** `Enums.DepositPeriodFrequency.Days` — the admin's tenor is typed in days. */
const TENOR_UNIT_DAYS = 0;

// Step validation and navigation
export const validateStep = (step: number, form: any) => {
  return async (dispatch: any, getState: any) => {
    try {
      const state = getState();
      const { formData, walletBalance } = state.investmentFormReducer;

      if (step === 0) {
        // Validate Step 1 (Funder Selection)
        await form.validateFields([
          "funder_id",
          "investment_product_id",
          "currency",
          "raw_funder_id",
          "relationship_manager_id", // Add relationship manager validation
        ]);

        // Update state with values from step 1
        const stepOneValues = form.getFieldsValue([
          "raw_funder_id",
          "funder_id",
          "currency",
        ]);

        // Get funder_id - check if it's an object or raw value
        let funderId = stepOneValues.raw_funder_id;
        if (
          !funderId &&
          typeof stepOneValues.funder_id === "object" &&
          stepOneValues.funder_id?.value
        ) {
          funderId = stepOneValues.funder_id.value;
        } else if (!funderId) {
          funderId = stepOneValues.funder_id;
        }

        // Get the investment product detail ID from Redux store
        const investmentProductDetailId = formData.investment_product_detail_id;

        // Call the wallet validation API
        try {
          // Show loading state while validating
          form.setFieldValue("walletLoading", true);

          await getDataWithDotNet(
            `${ajaxEndpoints.VALIDATE_CUSTOMER_WALLET}?customerId=${funderId}&investmentProductDetailId=${investmentProductDetailId}`,
          );

          // First API call was successful (200 OK)
          // But keep loading state active while we fetch wallets

          // Get relationship manager ID
          const relationshipManagerId = form.getFieldValue(
            "relationship_manager_id",
          );

          // Update Redux state
          dispatch(
            updateFormData({
              funder_id: Number(funderId),
              raw_funder_id: Number(funderId),
              currency: stepOneValues.currency,
              investment_product_detail_id: investmentProductDetailId,
              relationship_manager_id: relationshipManagerId,
            }),
          );

          // Fetch customer wallets for the dropdown in step 2
          try {
            const walletsResponse = await getDataWithDotNet(
              `${ajaxEndpoints.GET_CUSTOMER_WALLETS}?customerId=${funderId}&investmentProductDetailId=${investmentProductDetailId}`,
            );

            // Store the wallets in the Redux store
            if (walletsResponse.data && walletsResponse.data.wallets) {
              dispatch(setAvailableWallets(walletsResponse.data.wallets));
            } else if (
              walletsResponse.data &&
              Array.isArray(walletsResponse.data)
            ) {
              // Handle case where response might be an array directly
              dispatch(setAvailableWallets(walletsResponse.data));
            }
          } catch (error) {
            message.warning(
              "Could not fetch wallet details. You may need to select a wallet again.",
            );
          } finally {
            // Now both API calls are complete, turn off the loading state
            form.setFieldValue("walletLoading", false);
          }
        } catch (error) {
          const errorMessage =
            errorHandler(error) ||
            "Failed to validate wallet. Please try again.";
          message.error(errorMessage);

          // Set field-level error message to make it clearer to the user what happened
          form.setFields([
            {
              name: "funder_id",
              errors: [errorMessage],
            },
          ]);

          form.setFieldValue("walletLoading", false);
          return false;
        }
      } else if (step === 1) {
        // Validate Step 2 (Amount Details)
        await form.validateFields([
          "wallet_id",
          "principal_amount",
          "effective_date",
          "transaction_receipt",
          "amount_to_credit",
        ]);

        // Get form values for validation
        const principalAmount = Number(
          form.getFieldValue("principal_amount") || 0,
        );
        const amountToCredit = Number(
          form.getFieldValue("amount_to_credit") || 0,
        );
        const selectedWalletId = form.getFieldValue("wallet_id");

        // Get the selected wallet from Redux store
        const { availableWallets } = state.investmentFormReducer;
        const selectedWallet = availableWallets.find(
          (wallet: { id: any }) => wallet.id === selectedWalletId,
        );
        const selectedWalletBalance = selectedWallet?.availableBalance || 0;

        // Calculate required top-up based on investment amount and selected wallet balance
        const requiredTopUp = Math.max(
          0,
          principalAmount - selectedWalletBalance,
        );

        // Validate that top-up amount is sufficient if required
        if (requiredTopUp > 0 && amountToCredit < requiredTopUp) {
          form.setFields([
            {
              name: "amount_to_credit",
              errors: [
                `Wallet top-up amount must be at least ${requiredTopUp.toLocaleString()} ${selectedWallet?.currency || "NGN"} to proceed with this investment`,
              ],
            },
          ]);
          throw new Error("Insufficient top-up amount");
        }

        // Get and update all step 2 values
        const stepTwoValues = form.getFieldsValue([
          "principal_amount",
          "effective_date",
          "amount_to_credit",
        ]);

        // Update Redux state
        dispatch(
          updateFormData({
            principal_amount: Number(stepTwoValues.principal_amount || 0),
            amount_to_credit: Number(stepTwoValues.amount_to_credit || 0),
            effective_date: stepTwoValues.effective_date?.format("YYYY-MM-DD"),
          }),
        );

        // Calculate interest when moving to step 3
        await dispatch(calculateInterest());
      }

      // Move to next step
      dispatch(setCurrentStep(step + 1));
      return true;
    } catch (error) {
      return false;
    }
  };
};

// Form submission
export const submitInvestmentForm = (
  form: any,
  data: any,
  onSubmit: Function,
) => {
  return async (dispatch: any, getState: any) => {
    try {
      // Validate all fields
      await form.validateFields();

      const state = getState();
      const { formData, walletBalance, transactionReceipt } =
        state.investmentFormReducer;

      if (!transactionReceipt.base64) {
        form.setFields([
          {
            name: "transaction_receipt",
            errors: ["Please upload transaction receipt"],
          },
        ]);
        return false;
      }
      // Get latest form values
      const currentFormValues = form.getFieldsValue(true);
      dispatch(updateFormData(currentFormValues));

      // Validate wallet balance vs investment amount
      const principalAmount = Number(currentFormValues.principal_amount || 0);
      const amountToCredit = Number(currentFormValues.amount_to_credit || 0);
      const selectedWalletId = currentFormValues.wallet_id;

      // Get the selected wallet from Redux store
      const { availableWallets } = state.investmentFormReducer;
      const selectedWallet = availableWallets.find(
        (wallet: { id: any }) => wallet.id === selectedWalletId,
      );
      const selectedWalletBalance = selectedWallet?.availableBalance || 0;

      // Calculate required top-up based on the selected wallet's balance
      const requiredTopUp = Math.max(
        0,
        principalAmount - selectedWalletBalance,
      );

      // Validate top-up is sufficient
      if (requiredTopUp > 0 && amountToCredit < requiredTopUp) {
        form.setFields([
          {
            name: "amount_to_credit",
            errors: [
              `Wallet top-up amount must be at least ${requiredTopUp.toLocaleString()} ${selectedWallet?.currency || "NGN"} to proceed with this investment`,
            ],
          },
        ]);

        // Focus on the step with the error
        dispatch(setCurrentStep(1));
        return false;
      }

      // Determine investment product detail
      const investmentProductDetail = data.investment_product_details?.find(
        (item: any) => item.currency_code === formData.currency,
      );

      // Ensure we have a funder ID
      let funderId = formData.raw_funder_id || formData.funder_id;

      // Build the final submission data
      const submitData: IAdminLogInvestmentRequest = {
        funder_id: Number(funderId),
        interest_calculation_days_in_year_type:
          formData.number_of_days_in_year || 365,
        investment_product_details_id: Number(
          investmentProductDetail?.id || data?.id,
        ),
        principal_amount: Number(formData.principal_amount),
        interest_rate: Number(formData.interest_rate),
        tenure: Number(formData.tenure),
        tenor_unit: TENOR_UNIT_DAYS,
        currency: formData.currency,
        interest_repayment_type: formData.interest_repayment_type,
        effective_date: formData.effective_date,
        base64_file_string: transactionReceipt.base64,
        filename_with_extension: transactionReceipt.fileName,
        amount_to_credit: Number(formData.amount_to_credit),
        external_wallet_id: selectedWalletId,
        relationship_manager_id: formData.relationship_manager_id,
        override_chart_interest:
          isDynamicDeposit(investmentProductDetail?.deposit_type) &&
          Boolean(formData.override_chart_interest),
      };

      // Submit the data
      onSubmit(submitData);
      return true;
    } catch (error) {
      return false;
    }
  };
};

// Calculate interest with loading state management
export const calculateInterestWithLoading = (
  setLoading: (loading: boolean) => void,
) => {
  return async (dispatch: any, getState: any) => {
    try {
      // Get current form values from state
      const state = getState();
      const { formData } = state.investmentFormReducer;

      // Ensure we have the required values
      if (
        !formData.tenure ||
        !formData.interest_repayment_type ||
        !formData.principal_amount
      ) {
        return false;
      }

      setLoading(true);

      // Call the API to calculate interest
      const response = await postDataWithDotNet(
        ajaxEndpoints.CALCULATE_INVESTMENT_INTEREST,
        {
          interest_rate: formData.interest_rate,
          principal_amount: Number(formData.principal_amount),
          tenure: Number(formData.tenure),
          start_date: formData.effective_date || new Date().toISOString(),
          interest_repayment_type: formData.interest_repayment_type,
          number_of_days_in_year: formData.number_of_days_in_year || 365,
        },
      );

      const result = {
        interest_calculated: response.data.interest_amount,
        maturity_amount: response.data.maturity_amount,
        monthly_interest_payable: response.data.monthly_interest_payable,
        monthly_interest_accruals: response.data.monthly_interest_accruals,
      };

      // Dispatch action to update interest calculation in store
      dispatch({
        type: "INVESTMENT_FORM_CALCULATE_INTEREST_SUCCESS",
        payload: result,
      });

      setLoading(false);
      return true;
    } catch (error) {
      appInsights.trackException({
        exception: error as Error,
        properties: {
          fileName: "investmentFormSubmission.ts",
        },
      });
      setLoading(false);
      return false;
    }
  };
};
